import moment, { Moment } from 'moment-timezone';
import { FunctionComponent, useMemo, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTimeZone } from 'hooks';
import { getWeeklyBreakdownReport } from 'actions/insights/bookings/weeklyBreakdownReport/WeeklyBreakdownReportActions';
import styles from './WeeklyBreakdown.module.scss';
import { Period } from '@spike/model';
import { RootState } from 'store';

import Header from '../../components/Header';
import ToggleTabs from 'components/Insights/components/ToogleTabs/ToggleTabs';
import WeeklyBreakdownTable from './WeeklyBreakdownTable';
import WeeklyBreakdownCharts from './WeeklyBreakdownCharts';

import type { WeeklyBreakdownReport, WeekDayKey, WeekValues } from 'model/BookingsWeeklyBreakdownReport';
import { useDateRangeOptions } from 'hooks/useDateRangeOptions';

interface WeeklyBreakdownProps {
    description?: string;
}

const toDateParam = (m: Moment) => m.format('YYYY-MM-DD');

const options = [
    { id: 'table', label: 'Table' },
    { id: 'chart', label: 'Chart' }
];

const DAY_KEYS: WeekDayKey[] = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

const fmtCurrency = (n: number) =>
    n.toLocaleString(undefined, { style: 'currency', currency: 'USD', minimumFractionDigits: 0 });

const fmtPct = (n: number) => `${n}%`;

const toDisplayDate = (m?: Moment) => (m ? m.format('ddd, [[]MM/DD/YYYY[]]') : '');

const getWeekValue = (v: WeekValues | undefined, key: WeekDayKey | 'total') => {
    if (!v) return 0;
    return (v as any)[key] ?? 0;
};

export const WeeklyBreakdown: FunctionComponent<WeeklyBreakdownProps> = props => {
    const dispatch = useDispatch();
    const timeZone = useTimeZone();
    const { report, loading } = useSelector((s: RootState) => s.weeklyBreakdownReport);
    const { thisWeekOption, nextWeekOption, lastWeekOption } = useDateRangeOptions();

    const [currentTab, setCurrentTab] = useState('table');

    const defaultPeriod: Period = useMemo(() => {
        const now = moment().tz(timeZone);
        return {
            from: now.clone().startOf('week'),
            to: now.clone().endOf('week')
        };
    }, [timeZone]);

    const [period, setPeriod] = useState<Period | null>(defaultPeriod);
    const [allTime, setAllTime] = useState(false);

    const dateRangeOptions = [thisWeekOption, nextWeekOption, lastWeekOption];

    useEffect(() => {
        if (!period) {
            dispatch(getWeeklyBreakdownReport());
            return;
        }
        dispatch(getWeeklyBreakdownReport(toDateParam(period.from), toDateParam(period.to)));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const calendar = {
        optionSelected: 'This Week'
    };

    const handleChangePeriod = (p: Period | null, isAllTime: boolean) => {
        setAllTime(isAllTime);

        if (isAllTime || !p) {
            setPeriod(null);
            dispatch(getWeeklyBreakdownReport());
            return;
        }

        setPeriod({ from: p.from, to: p.to });
        dispatch(getWeeklyBreakdownReport(toDateParam(p.from), toDateParam(p.to)));
    };

    const exportFile = useMemo(() => {
        const r = report as WeeklyBreakdownReport | undefined;

        const headers: string[] = ['Metric', ...DAY_KEYS.map(k => toDisplayDate(r?.days?.[k])), 'Weekly Total'];

        const rows: string[][] = [];

        // 1) Total Bookings (count)
        rows.push([
            'Total Bookings',
            ...DAY_KEYS.map(k => String(getWeekValue(r?.totalBookings, k))),
            String(getWeekValue(r?.totalBookings, 'total'))
        ]);

        // 2) % of Weekly Bookings (percent)
        rows.push([
            '% of Weekly Bookings',
            ...DAY_KEYS.map(k => fmtPct(getWeekValue(r?.bookingsPct, k) as number)),
            fmtPct(getWeekValue(r?.bookingsPct, 'total') as number)
        ]);

        // 3) Completed (%) (countPercent)
        rows.push([
            'Completed (%)',
            ...DAY_KEYS.map(k => {
                const cell = r?.completed?.values?.[k];
                if (!cell) return '0';
                return cell.pct != null ? `${cell.value} (${fmtPct(cell.pct)})` : String(cell.value);
            }),
            String(r?.completed?.total ?? 0)
        ]);

        // 4) Canceled (%) (countPercent)
        rows.push([
            'Canceled (%)',
            ...DAY_KEYS.map(k => {
                const cell = r?.canceled?.values?.[k];
                if (!cell) return '0';
                return cell.pct != null ? `${cell.value} (${fmtPct(cell.pct)})` : String(cell.value);
            }),
            String(r?.canceled?.total ?? 0)
        ]);

        // 5) No-Show (%) (countPercent)
        rows.push([
            'No-Show (%)',
            ...DAY_KEYS.map(k => {
                const cell = r?.noShow?.values?.[k];
                if (!cell) return '0';
                return cell.pct != null ? `${cell.value} (${fmtPct(cell.pct)})` : String(cell.value);
            }),
            String(r?.noShow?.total ?? 0)
        ]);

        // 6) Total Pets (count)
        rows.push([
            'Total Pets',
            ...DAY_KEYS.map(k => String(getWeekValue(r?.totalPets, k))),
            String(getWeekValue(r?.totalPets, 'total'))
        ]);

        // 7) Total Clients (count)
        rows.push([
            'Total Clients',
            ...DAY_KEYS.map(k => String(getWeekValue(r?.totalClients, k))),
            String(getWeekValue(r?.totalClients, 'total'))
        ]);

        // 8) Net Sales (currency)
        rows.push([
            'Net Sales',
            ...DAY_KEYS.map(k => fmtCurrency(getWeekValue(r?.netSales, k) as number)),
            fmtCurrency(getWeekValue(r?.netSales, 'total') as number)
        ]);

        // 9) % of Sales (percent)
        rows.push([
            '% of Sales',
            ...DAY_KEYS.map(k => fmtPct(getWeekValue(r?.salesPct, k) as number)),
            fmtPct(getWeekValue(r?.salesPct, 'total') as number)
        ]);

        return {
            name: 'Weekly breakdown report.csv',
            dataInMemory: true,
            headers,
            dataToExport: rows
        };
    }, [report]);

    return (
        <div className={styles.container}>
            <div>
                <span className={styles.description}> {props.description} </span>
            </div>

            <div className={styles.categoryContainer}>
                <ToggleTabs
                    options={options}
                    value={currentTab}
                    setValue={setCurrentTab}
                />
            </div>

            <div>
                <Header
                    period={period}
                    onChangePeriod={handleChangePeriod}
                    exportFile={exportFile}
                    calendar={calendar}
                    hideAllTime={true}
                    dateOptions={dateRangeOptions}
                />
            </div>

            {currentTab === 'table' && (
                <div className={styles.tableContainer}>
                    <WeeklyBreakdownTable
                        loading={loading}
                        report={report}
                    />
                </div>
            )}

            {currentTab === 'chart' && (
                <div className={styles.chartContainer}>
                    <WeeklyBreakdownCharts
                        loading={loading}
                        report={report}
                    />
                </div>
            )}
        </div>
    );
};

export default WeeklyBreakdown;
